/* =============================================================

   NAVBAR — Sticky navigation with phone CTA always visible

   Design: Deep navy background, gold CTA, transparent on hero scroll

   ============================================================= */

import { useState, useEffect } from "react";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { LOGOS_OPTIMIZED, LOGO_DIMENSIONS } from "@/const/logosOptimized";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState<string | null>(null);

  useEffect(() => {
    let rafId: number;
    let lastScrollY = 0;

    const handleScroll = () => {
      // Batch scroll updates with RAF to prevent forced reflow
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        const scrolled = lastScrollY > 60;
        setIsScrolled(scrolled);
        
        // Calculate scroll progress for visual feedback
        const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
        const progress = totalScroll > 0 ? (lastScrollY / totalScroll) * 100 : 0;
        setScrollProgress(progress);
      });

      lastScrollY = window.scrollY;
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const navLinks = [
    { label: "Home", href: "/" },
    { 
      label: "Services", 
      href: "/#services",
      submenu: [
        { label: "Bookkeeper Perth", href: "/bookkeeper-perth" },
        { label: "Bookkeeping Services", href: "/bookkeeping-services-perth" },
        { label: "BAS Agent", href: "/bas-agent-perth" },
        { label: "Xero Bookkeeping", href: "/xero-bookkeeper-perth" },
        { label: "Payroll Bookkeeping", href: "/payroll-bookkeeper-perth" },
        { label: "Catch-up Bookkeeping", href: "/catch-up-bookkeeping-perth" },
        { label: "Bookkeeping for Tradies", href: "/bookkeeping-for-tradies-perth" },
        { label: "Bookkeeping Fees", href: "/bookkeeping-fees-perth" },
      ]
    },
    { label: "Pricing", href: "/#pricing" },
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/#contact" },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[oklch(0.22_0.06_250)] shadow-lg shadow-[oklch(0.22_0.06_250)/0.3]"
            : "bg-[oklch(0.22_0.06_250)/0.95] backdrop-blur-md"
        }`}
      >
        {/* Scroll progress indicator */}
        <div
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[oklch(0.72_0.12_75)] to-[oklch(0.72_0.12_75)/0.5] transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
        <div className="container">
          <div className="flex items-center justify-between h-16 lg:h-18">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 group hover:opacity-80 transition-opacity">
              <picture>
                <source srcSet={LOGOS_OPTIMIZED.standard.webp} type="image/webp" />
                <img 
                  src={LOGOS_OPTIMIZED.standard.png}
                  srcSet={`${LOGOS_OPTIMIZED.standard.png} 1x, ${LOGOS_OPTIMIZED.retina.png} 2x`}
                  alt="The Perth Bookkeeper" 
                  className="w-8 h-8 flex-shrink-0 group-hover:scale-105 transition-transform"
                  width={LOGO_DIMENSIONS.standard.width}
                  height={LOGO_DIMENSIONS.standard.height}
                  loading="eager"
                  decoding="async"
                />
              </picture>
              <div className="leading-tight">
                <div className="text-white font-semibold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  The Perth Bookkeeper
                </div>
                <div className="text-[oklch(0.72_0.12_75)] text-xs" style={{ fontFamily: "'DM Mono', monospace", letterSpacing: '0.08em' }}>
                  BAS AGENT · XERO CERTIFIED
                </div>
              </div>
            </a>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              {navLinks.map((link) => (
                <div key={link.label} className="relative group">
                  <a
                    href={link.href}
                    className="text-white/80 hover:text-[oklch(0.72_0.12_75)] text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {link.label}
                    {link.submenu && <ChevronDown size={14} className="group-hover:rotate-180 transition-transform" />}
                  </a>
                  {link.submenu && (
                    <div className="absolute left-0 mt-0 w-56 bg-[oklch(0.18_0.05_250)] border border-white/10 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2 z-50">
                      {link.submenu.map((item) => (
                        <a
                          key={item.label}
                          href={item.href}
                          className="block px-4 py-2 text-white/80 hover:text-[oklch(0.72_0.12_75)] text-sm transition-colors"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Right: Phone + CTA */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <a
                href="tel:0424200785"
                className="flex items-center gap-1.5 text-white/80 hover:text-[oklch(0.72_0.12_75)] transition-colors text-sm whitespace-nowrap"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <Phone size={14} className="flex-shrink-0" />
                <span>0424 200 785</span>
              </a>
              <a href="/#contact" className="btn-gold text-sm py-2 px-4 shadow-lg shadow-[oklch(0.72_0.12_75)/0.3] hover:shadow-xl hover:shadow-[oklch(0.72_0.12_75)/0.4] transition-all hover:scale-105 whitespace-nowrap">
                <span className="hidden xl:inline">Book Free Consultation</span>
                <span className="xl:hidden">Book a Call</span>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden text-white p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-[oklch(0.18_0.05_250)] border-t border-white/10">
            <div className="container py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <div key={link.label}>
                  <button
                    onClick={() => setExpandedSubmenu(expandedSubmenu === link.label ? null : link.label)}
                    className="w-full text-left text-white/80 hover:text-[oklch(0.72_0.12_75)] py-3 text-base font-medium border-b border-white/5 transition-colors flex items-center justify-between"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    <a href={link.href} onClick={() => setMobileOpen(false)}>
                      {link.label}
                    </a>
                    {link.submenu && (
                      <ChevronDown size={16} className={`transition-transform ${expandedSubmenu === link.label ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                  {link.submenu && expandedSubmenu === link.label && (
                    <div className="bg-[oklch(0.15_0.04_250)] pl-4">
                      {link.submenu.map((item) => (
                        <a
                          key={item.label}
                          href={item.href}
                          className="text-white/60 hover:text-[oklch(0.72_0.12_75)] py-2 text-sm font-medium border-b border-white/5 transition-colors block"
                          style={{ fontFamily: "'DM Sans', sans-serif" }}
                          onClick={() => {
                            setMobileOpen(false);
                            setExpandedSubmenu(null);
                          }}
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 flex flex-col gap-3">
                <a
                  href="tel:0424200785"
                  className="flex items-center gap-2 text-white/80 text-sm"
                >
                  <Phone size={14} />
                  <span>0424 200 785</span>
                </a>
                <a href="/#contact" className="btn-gold text-sm justify-center hover:scale-105 transition-transform" onClick={() => setMobileOpen(false)}>
                  Book Free Consultation
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
